function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


class MenuProductSlider extends HTMLElement {
  constructor() {
    super();
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.swiperInit();
  }
  swiperInit() {
    const mbSlidesPerView = this.dataset.mbSlidesPerView || 1.1;
    const mbSpaceBetween = this.dataset.mbSpaceBetween || 10;
    const swiper = new Swiper(this, {
      slidesPerView: parseFloat(mbSlidesPerView) || mbSlidesPerView,
      spaceBetween: parseInt(mbSpaceBetween),
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      breakpoints: {
        750: {
          slidesPerView: 3,
        },
        992: {
          slidesPerView: 4,
        },
      }
    });
  }
}
if (!window.customElements.get('menu-product-slider')) {
  window.customElements.define('menu-product-slider', MenuProductSlider);
}


class BannerSlider extends HTMLElement {
  constructor() {
    super();
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.pagination = this.querySelector('.slide-pagination');
    this.enableAutoplay = this.dataset.autoplay === 'true';
    this.autoplayDelay = parseInt(this.dataset.delay) || 5000;
    this.swiperInit();
  }
  swiperInit() {
    const self = this;
    const swiperConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      autoHeight: true,
      grabCursor: true,
      simulateTouch: true,
      pagination: {
        el: this.pagination,
        clickable: true,
        renderBullet: function (index, className) {
          const size = 20;
          const strokeWidth = 2;
          const radius = (size - strokeWidth) / 2;
          const circumference = 2 * Math.PI * radius;
          return `<span class="${className}">
            <svg viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
              <circle class="progress-ring-bg" cx="${size/2}" cy="${size/2}" r="${radius}" stroke-width="${strokeWidth}" />
              <circle class="progress-ring-fill" cx="${size/2}" cy="${size/2}" r="${radius}" stroke-width="${strokeWidth}" 
                stroke-dasharray="${circumference}" stroke-dashoffset="${circumference}" />
            </svg>
          </span>`;
        }
      },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      on: {
        init: function(swiper) {
          self.updateRingProgress(swiper);
        },
        slideChangeTransitionStart: function(swiper) {
          self.resetAllRings(swiper);
        },
        slideChangeTransitionEnd: function(swiper) {
          self.animateRingProgress(swiper);
        },
        autoplayTimeLeft(s, time, progress) {
          // progress: 0=刚开始, 1=即将结束
          // 圆环从满圈(0) → 空圈(circumference) 倒数
          const activeBullet = self.pagination.querySelector('.swiper-pagination-bullet-active');
          if (activeBullet) {
            const ring = activeBullet.querySelector('.progress-ring-fill');
            if (ring) {
              const circumference = 2 * Math.PI * ((20 - 2) / 2);
              ring.style.strokeDashoffset = circumference * progress;
            }
          }
        }
      }
    };

    if (this.enableAutoplay) {
      swiperConfig.autoplay = {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
      };
    }

    this.swiper = new Swiper(this, swiperConfig);
  }

  /* 设置当前 active bullet 的环为满圈 */
  updateRingProgress(swiper) {
    const activeBullet = this.pagination.querySelector('.swiper-pagination-bullet-active');
    if (activeBullet) {
      const ring = activeBullet.querySelector('.progress-ring-fill');
      if (ring) {
        ring.style.strokeDashoffset = '0';
      }
    }
  }

  /* 先重置所有环为 "空" */
  resetAllRings(swiper) {
    const rings = this.pagination.querySelectorAll('.progress-ring-fill');
    const circumference = 2 * Math.PI * ((20 - 2) / 2);
    rings.forEach(function(ring) {
      ring.style.strokeDashoffset = circumference;
    });
  }

  /* 过渡结束后把当前 active 的环置为满圈 */
  animateRingProgress(swiper) {
    this.updateRingProgress(swiper);
  }
}
if (!window.customElements.get('banner-slider')) {
  window.customElements.define('banner-slider', BannerSlider);
}


class ProductListSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.swiperWrapper = this.querySelector('.swiper-wrapper');
    this.progressBar = this.querySelector('.slider-progress-bar');
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.swiperInit();
    this.setupResizeObserver();
  }

  swiperInit() {
    // Only init Swiper on desktop (md breakpoint = 768px)
    if (window.innerWidth < 768) return;
    if (!this.progressBar || !this.nextButton || !this.prevButton) return;

    // Remove mobile grid classes before Swiper init
    if (this.swiperWrapper) {
      this.swiperWrapper.style.display = '';
    }

    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      spaceBetween: 24,
      pagination: {
        el: this.progressBar,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      breakpoints: {
        750: {
          slidesPerView: 2,
        },
        992: {
          slidesPerView: 2.3,
        },
      }
    });
  }

  setupResizeObserver() {
    let wasMobile = window.innerWidth < 768;
    window.addEventListener('resize', () => {
      const isMobile = window.innerWidth < 768;
      if (wasMobile && !isMobile && !this.swiper) {
        // Switched from mobile to desktop: init Swiper
        this.swiperInit();
      } else if (!wasMobile && isMobile && this.swiper) {
        // Switched from desktop to mobile: destroy Swiper, restore grid
        this.swiper.destroy(true, true);
        this.swiper = null;
        // Reset wrapper display so CSS grid takes over
        if (this.swiperWrapper) {
          this.swiperWrapper.style.display = '';
        }
      } else if (this.swiper) {
        this.swiper.update();
      }
      wasMobile = isMobile;
    });
  }
}
if (!window.customElements.get('product-list-slider')) {
  window.customElements.define('product-list-slider', ProductListSlider);
}


class VideosSlider extends HTMLElement {
  constructor() {
    super();
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.pagination = this.querySelector('.slider-pagination');
    this.videoSlides = this.querySelectorAll('.swiper-slide');
    this.swiper = null;
    this.swiperInit();
    this.setupVideoPlayTriggers();
  }

  swiperInit() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      spaceBetween: 10,
      pagination: {
        el: this.pagination,
        clickable: true,
      },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      breakpoints: {
        320: {
          slidesPerView: 'auto',
          spaceBetween: 30,
        },
        750: {
          slidesPerView: 3,
        },
        992: {
          slidesPerView: 4,
        },
        1199: {
          slidesPerView: 5,
        },
      },
      on: {
        slideChange: () => {
          this.pauseAllVideos();
        },
      },
    });
  }

  setupVideoPlayTriggers() {
    this.videoSlides.forEach((slide) => {
      const playTrigger = slide.querySelector('.video-play-trigger');
      const videoElement = slide.querySelector('video');
      
      if (!playTrigger || !videoElement) return;

      // Click to play/pause
      playTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleVideoPlay(videoElement, playTrigger);
      });

      // Video event listeners
      videoElement.addEventListener('play', () => {
        this.handleVideoPlay(videoElement, playTrigger);
      });

      videoElement.addEventListener('pause', () => {
        this.handleVideoPause(playTrigger);
      });

      videoElement.addEventListener('ended', () => {
        this.handleVideoEnded(playTrigger);
      });
    });
  }

  toggleVideoPlay(video, playTrigger) {
    if (video.paused || video.ended) {
      // Pause all other videos first
      this.pauseAllVideos();
      video.play().catch(error => {
        console.error('Error playing video:', error);
      });
    } else {
      video.pause();
    }
  }

  pauseAllVideos() {
    this.videoSlides.forEach(slide => {
      const video = slide.querySelector('video');
      const playTrigger = slide.querySelector('.video-play-trigger');
      if (video && !video.paused) {
        video.pause();
      }
      if (playTrigger) {
        playTrigger.style.display = 'block';
      }
    });
  }

  handleVideoPlay(video, playTrigger) {
    playTrigger.style.display = 'none';
    video.setAttribute('controls', 'true');
  }

  handleVideoPause(playTrigger) {
    playTrigger.style.display = 'block';
  }

  handleVideoEnded(playTrigger) {
    playTrigger.style.display = 'block';
  }
}

if (!window.customElements.get('videos-slider')) {
  window.customElements.define('videos-slider', VideosSlider);
}

class TabSystem extends HTMLElement {
  constructor() {
    super();
    this._handleClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  init() {
    this.tabNav = this.querySelector('tab-nav');
    this.tabContent = this.querySelector('tab-content');
    
    if (!this.tabNav || !this.tabContent) {
      console.warn('TabSystem requires both tab-nav and tab-content elements');
      return;
    }
    
    this.tabButtons = [...this.tabNav.children];
    this.contentPanels = [...this.tabContent.children];
    
    if (this.tabButtons.length !== this.contentPanels.length) {
      console.warn('Number of tabs does not match number of content panels');
      return;
    }
    
    // Remove existing event listeners
    this.removeEventListeners();
    
    // Add new event listeners
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('click', (e) => this._handleClick(e, index));
    });
    
    // Activate first tab by default
    if (this.tabButtons.length > 0) {
      this.activateTab(0);
    }
  }

  _handleClick(e, index) {
    e.preventDefault();
    this.activateTab(index, { scroll: true });
  }

  activateTab(index, { scroll = false } = {}) {
    // Remove active class from all tabs and content
    this.tabButtons.forEach(btn => btn.classList.remove('active'));
    this.contentPanels.forEach(panel => panel.classList.remove('active'));
    
    // Add active class to clicked tab and corresponding content
    this.tabButtons[index].classList.add('active');
    this.contentPanels[index].classList.add('active');
    
    // Only scroll on user click, not on initial page load
    if (scroll) {
      this.tabButtons[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
    
    // Dispatch custom event for any external listeners
    this.dispatchEvent(new CustomEvent('tab-change', {
      detail: { index, tab: this.tabButtons[index] }
    }));
  }

  removeEventListeners() {
    if (this.tabButtons) {
      this.tabButtons.forEach(button => {
        button.removeEventListener('click', this._handleClick);
      });
    }
  }

  // Optional: Add a method to refresh if content changes
  refresh() {
    this.init();
  }
}

// Register the custom element
if (!window.customElements.get('tab-system')) {
  window.customElements.define('tab-system', TabSystem);
}


class TestimonialSlider extends HTMLElement {
  constructor() {
    super();
    this.logoPagination = this.querySelectorAll('.logo-item');
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.pagination = this.querySelector('.slide-pagination');
    this.swiperInit();
  }
  swiperInit() {
    let logoItems = this.logoPagination;
    const swiper = new Swiper(this, {
      loop: true,
      slidesPerView:1,
      pagination: {
        el: this.pagination,
        clickable: true,
      },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      on: {
          init: function() {
            if(logoItems != null){
              updateActiveLogo(this.realIndex);
            }
          },
          slideChange: function() {
            if(logoItems != null){
              updateActiveLogo(this.realIndex);
            }
          }
      }
    });

    function updateActiveLogo(index) {
        logoItems.forEach(item => {
            item.classList.remove('active-logo');
        });
        
        if (logoItems[index]) {
            logoItems[index].classList.add('active-logo');
        }
    }
    if(logoItems != null){
      logoItems.forEach((item, index) => {
          item.addEventListener('click', function() {
            console.log('clicked')
              swiper.slideTo(index);
          });
      });
    }
    
  }
}
if (!window.customElements.get('testimonial-slider')) {
  window.customElements.define('testimonial-slider', TestimonialSlider);
}


class InstagramSlider extends HTMLElement {
  constructor() {
    super();
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.progressBar = this.querySelector('.slider-progress-bar');
    this.swiperInit();
  }
  swiperInit() {
    const swiper = new Swiper(this.querySelector('.swiper'), {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 16,
      loop: true,
      autoplay: {
        delay: 2000,
      },
      pagination: {
        el: this.progressBar,
        type: "progressbar",
      },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      breakpoints: {
        750: {
          slidesPerView: 2,
          centeredSlides: false,
          spaceBetween: 24,
          loop: false,
        },
        992: {
          slidesPerView: 3,
          centeredSlides: false,
          spaceBetween: 24,
          loop: false,
        },
        1199: {
          slidesPerView: 4,
          centeredSlides: false,
          spaceBetween: 24,
          loop: false,
        }
      },
    });
  }
}
if (!window.customElements.get('instagram-slider')) {
  window.customElements.define('instagram-slider', InstagramSlider);
}

class AnnouncementSlider extends HTMLElement {
  constructor() {
    super();
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.swiperInit();
  }
  swiperInit() {
    const swiper = new Swiper(this, {
      slidesPerView: 1,
      loop: true,
      autoplay: {
        delay: 3000,
      },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
    });
  }
}
if (!window.customElements.get('annoucnement-slider')) {
  window.customElements.define('annoucnement-slider', AnnouncementSlider);
}


class ProductMediaSlider extends HTMLElement {
    constructor() {
      super();
      this.nextButton = this.querySelector('.main-slide-next-btn');
      this.prevButton = this.querySelector('.main-slide-prev-btn');
      this.pagination = this.querySelector('.main-slide-pagination');
      this.swiperInit();
      this.initVideoClickHandlers();
    }

    swiperInit() {
      this.mainSwiper = new Swiper(this, {
        slidesPerView: 1,
        spaceBetween: 0,
        centeredSlides: false,
        navigation: {
          nextEl: this.nextButton,
          prevEl: this.prevButton,
        },
        pagination: {
          el: this.pagination,
          clickable: true,
          dynamicBullets: true,
          dynamicMainBullets: 5,
        },
        on: {
          init: (swiper) => {
            // Load media for first slide
            this.loadSlideMedia(swiper.slides[0]);
            this.autoplayActiveVideo(swiper.slides[0]);
            if(swiper.activeIndex < 5){
              this.classList.add('lessthanfive');
            }else{
              this.classList.remove('lessthanfive');
            }
            // Badge only visible on first slide
            var badge = this.querySelector('.badge-svg-diy');
            if (badge) {
              badge.classList.toggle('badge-hidden', swiper.activeIndex !== 0);
            }
          },
          slideChangeTransitionStart: (swiper) => {
            // 只暂停即将离开的视频，避免 iPhone 布局重排时把当前主图视频拆掉
            const activeSlide = swiper.slides[swiper.activeIndex];
            this.pauseAllVideos(activeSlide);
          },
          slideChange: (swiper) => {
            // Update thumbnail active state
            const thumbnailSlider = document.querySelector('product-media-thumbnails');
            if (thumbnailSlider) {
              if (thumbnailSlider.thumbnailSwiper) {
                thumbnailSlider.thumbnailSwiper.slideTo(swiper.activeIndex);
              }
              if (typeof thumbnailSlider.updateActiveThumbnail === 'function') {
                thumbnailSlider.updateActiveThumbnail(swiper.activeIndex);
              }
            }

            if(swiper.activeIndex <= 5){
              this.classList.add('lessthanfive');
            }else{
              this.classList.remove('lessthanfive');
            }
            
            // Badge only visible on first slide
            var badge = this.querySelector('.badge-svg-diy');
            if (badge) {
              badge.classList.toggle('badge-hidden', swiper.activeIndex !== 0);
            }
            
            // Load media for active slide
            this.loadSlideMedia(swiper.slides[swiper.activeIndex]);
            this.autoplayActiveVideo(swiper.slides[swiper.activeIndex]);
            
            // Reinitialize video click handlers for the new active slide
            setTimeout(() => {
              this.initVideoClickHandlers();
            }, 100);
          }
        }
      });

      // Store reference for thumbnail slider
      window.productMediaMainSwiper = this.mainSwiper;
    }

    initVideoClickHandlers() {
      // Remove existing event listeners to prevent duplicates
      const videoContainers = this.querySelectorAll('.video-container');
      videoContainers.forEach(container => {
        container.removeEventListener('click', this.handleVideoClick);
      });

      // Add click event listeners to all video containers
      videoContainers.forEach(container => {
        container.style.cursor = 'pointer';
        container.addEventListener('click', this.handleVideoClick.bind(this));
        
        // Add hover effects for better UX
        container.addEventListener('mouseenter', function() {
          this.style.opacity = '0.9';
        });
        
        container.addEventListener('mouseleave', function() {
          this.style.opacity = '1';
        });
      });
    }

    handleVideoClick(event) {
      const container = event.currentTarget;
      if (container.getAttribute('data-video-playing') === 'true') return;
      const videoType = container.getAttribute('data-video-type');
      
      if (videoType === 'native') {
        this.playNativeVideo(container);
      } else if (videoType === 'external') {
        this.playExternalVideo(container);
      }
    }

    autoplayActiveVideo(slide) {
      if (!slide) return;
      const container = slide.querySelector('.video-container');
      if (!container) return;
      const videoType = container.getAttribute('data-video-type');
      if (videoType === 'native') {
        this.playNativeVideo(container);
      } else if (videoType === 'external') {
        this.playExternalVideo(container);
      }
    }

    prepareInlineVideo(video) {
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.controls = false;
      video.removeAttribute('controls');
      video.classList.add('product-inline-video');
    }

    kickVideo(video) {
      if (!video || !video.isConnected || (!video.paused && !video.ended)) return;
      this.prepareInlineVideo(video);
      const pending = video.play();
      if (pending && typeof pending.catch === 'function') pending.catch(() => {});
    }

    startNativePlayback(video) {
      this.prepareInlineVideo(video);
      this.kickVideo(video);
      ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach((evt) => {
        video.addEventListener(evt, () => this.kickVideo(video));
      });
      [100, 400, 1000, 2000].forEach((delay) => {
        setTimeout(() => this.kickVideo(video), delay);
      });
    }

    playNativeVideo(container) {
      const existingVideo = container.querySelector('video');
      if (existingVideo) {
        Array.from(container.children).forEach((el) => {
          if (el.tagName === 'IMG' || el.tagName === 'DIV') el.style.display = 'none';
        });
        container.setAttribute('data-video-playing', 'true');
        this.startNativePlayback(existingVideo);
        return;
      }

      const poster = container.querySelector('img');
      const videoTemplate = container.querySelector('.video-template');
      
      if (videoTemplate && poster) {
        const video = document.createElement('video');
        const templateVideo = videoTemplate.content.querySelector('video');
        if (templateVideo && templateVideo.getAttribute('poster')) {
          video.setAttribute('poster', templateVideo.getAttribute('poster'));
        }
        video.className = 'product-inline-video sd:w-full sd:h-full sd:object-cover';
        this.prepareInlineVideo(video);
        if (templateVideo && templateVideo.getAttribute('src')) video.src = templateVideo.getAttribute('src');
        const sources = Array.from(videoTemplate.content.querySelectorAll('source')).sort((a, b) => {
          const aMp4 = (a.getAttribute('type') || '').includes('mp4') ? 0 : 1;
          const bMp4 = (b.getAttribute('type') || '').includes('mp4') ? 0 : 1;
          return aMp4 - bMp4;
        });
        sources.forEach((source) => {
          const node = document.createElement('source');
          node.src = source.getAttribute('src');
          node.type = source.getAttribute('type') || '';
          video.appendChild(node);
        });

        Array.from(container.children).forEach((el) => {
          if (el.tagName === 'IMG' || el.tagName === 'DIV') el.style.display = 'none';
        });
        
        container.setAttribute('data-video-playing', 'true');
        container.appendChild(video);
        this.startNativePlayback(video);
      }
    }

    playExternalVideo(container) {
      const videoHost = container.getAttribute('data-video-host');
      const videoId = container.getAttribute('data-video-id');
      const poster = container.querySelector('.video-poster');
      const playButtonOverlay = container.querySelector('.sd\\:absolute.sd-flex');
      
      if (!videoHost || !videoId) {
        console.warn('Missing video host or ID for external video');
        return;
      }
      if (container.getAttribute('data-video-playing') === 'true' && container.querySelector('iframe')) return;
      
      // Hide poster and play button
      if (poster) poster.style.display = 'none';
      if (playButtonOverlay) playButtonOverlay.style.display = 'none';
      Array.from(container.children).forEach((el) => {
        if (el.tagName === 'IMG' || el.tagName === 'DIV') el.style.display = 'none';
      });
      
      // Remove any existing iframe
      const existingIframe = container.querySelector('iframe');
      if (existingIframe) existingIframe.remove();
      
      let embedUrl = '';
      if (videoHost === 'youtube') {
        embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&enablejsapi=1`;
      } else if (videoHost === 'vimeo') {
        embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&title=0&byline=0&portrait=0`;
      }
      
      const iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.className = 'sd:w-full sd:h-full';
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', '');
      
      if (videoHost === 'youtube') {
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
      } else if (videoHost === 'vimeo') {
        iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      }
      
      container.appendChild(iframe);
      
      // Store reference to the playing video
      container.setAttribute('data-video-playing', 'true');
    }

    pauseAllVideos(activeSlide) {
      // Pause all native videos except the one still on screen
      const nativeVideos = this.querySelectorAll('video');
      nativeVideos.forEach(video => {
        if (activeSlide && activeSlide.contains(video)) return;
        if (!video.paused) {
          video.pause();
        }
      });

      // For external videos, we need to remove the iframes and show posters again
      const externalVideoContainers = this.querySelectorAll('.video-container[data-video-type="external"]');
      externalVideoContainers.forEach(container => {
        if (activeSlide && activeSlide.contains(container)) return;
        if (container.getAttribute('data-video-playing') === 'true') {
          const iframe = container.querySelector('iframe');
          const poster = container.querySelector('.video-poster');
          const playButtonOverlay = container.querySelector('.sd\\:absolute.sd-flex');
          
          if (iframe) {
            iframe.remove();
          }
          
          // Show poster and play button again
          if (poster) {
            poster.style.display = 'block';
          }
          if (playButtonOverlay) {
            playButtonOverlay.style.display = 'flex';
          }
          
          // Remove playing state
          container.removeAttribute('data-video-playing');
        }
      });

      // For native videos that were playing, show posters again
      const nativeVideoContainers = this.querySelectorAll('.video-container[data-video-type="native"]');
      nativeVideoContainers.forEach(container => {
        if (activeSlide && activeSlide.contains(container)) return;
        if (container.getAttribute('data-video-playing') === 'true') {
          const video = container.querySelector('video');
          const poster = container.querySelector('img');
          const playButtonOverlay = container.querySelector('.sd\\:absolute.sd-flex');
          
          if (video) {
            video.remove();
          }
          
          // Show poster and play button again
          if (poster) {
            poster.style.display = 'block';
          }
          if (playButtonOverlay) {
            playButtonOverlay.style.display = 'flex';
          }
          
          // Remove playing state
          container.removeAttribute('data-video-playing');
        }
      });
    }

    loadSlideMedia(slide) {
      if (!slide) return;
      
      // Check if slide has already been loaded
      if (slide.getAttribute('data-media-loaded') === 'true') return;
      
      // 视频由 autoplayActiveVideo 播放，这里只处理 3D 模型
      
      // Handle 3D model
      const modelContainer = slide.querySelector('.model-container');
      if (modelContainer) {
        const modelSrc = modelContainer.getAttribute('data-model-src');
        const modelPoster = modelContainer.getAttribute('data-model-poster');
        const poster = modelContainer.querySelector('.model-poster');
        
        if (modelSrc && poster) {
          const modelViewer = document.createElement('model-viewer');
          modelViewer.src = modelSrc;
          modelViewer.poster = modelPoster;
          modelViewer.className = 'sd:w-full sd:h-full';
          modelViewer.setAttribute('auto-rotate', '');
          modelViewer.setAttribute('camera-controls', '');
          modelViewer.setAttribute('shadow-intensity', '1');
          
          poster.style.display = 'none';
          modelContainer.querySelector('.sd\\:absolute').style.display = 'none';
          modelContainer.appendChild(modelViewer);
        }
      }
      
      // Mark slide as loaded
      slide.setAttribute('data-media-loaded', 'true');
    }
  }

  if (!window.customElements.get('product-media-slider')) {
    window.customElements.define('product-media-slider', ProductMediaSlider);
  }

  class ProductMediaThumbnails extends HTMLElement {
    constructor() {
      super();
      this.nextButton = this.querySelector('.thumbnail-next-btn');
      this.prevButton = this.querySelector('.thumbnail-prev-btn');
      this.swiperInit();
    }

    swiperInit() {
      const mediaCount = this.querySelectorAll('.swiper-slide').length;
      
      this.thumbnailSwiper = new Swiper(this, {
        direction: 'horizontal',
        slidesPerView: 9,
        spaceBetween: 16,
        navigation: {
          nextEl: this.nextButton,
          prevEl: this.prevButton,
        },
        breakpoints: {
          750: {
            direction: 'vertical',
          }
        },
        on: {
          init: () => {
            this.updateActiveThumbnail(0);
          }
        }
      });

      // Handle thumbnail clicks (事件委托，支持动态注入的变体缩略图)
      const thumbWrapper = this.querySelector('.swiper-wrapper');
      if (thumbWrapper) {
        thumbWrapper.addEventListener('click', (e) => {
          const thumbnail = e.target.closest('[data-thumbnail-index]');
          if (!thumbnail) return;
          const index = parseInt(thumbnail.getAttribute('data-thumbnail-index'));
          if (!isNaN(index) && window.productMediaMainSwiper) {
            window.productMediaMainSwiper.slideTo(index);
            this.updateActiveThumbnail(index);
          }
        });
      }

      // Handle window resize
      window.addEventListener('resize', () => {
        const newDirection = window.innerWidth >= 768 ? 'vertical' : 'horizontal';
        if (this.thumbnailSwiper.params.direction !== newDirection) {
          this.thumbnailSwiper.changeDirection(newDirection);
        }
      });

      // Store reference for thumbnail slider
      window.productMediaThumbSwiper = this.thumbnailSwiper;
    }

    updateActiveThumbnail(activeIndex) {
      const thumbnails = this.querySelectorAll('.swiper-slide[data-thumbnail-index]');
      thumbnails.forEach((thumbnail) => {
        const index = parseInt(thumbnail.getAttribute('data-thumbnail-index'), 10);
        const isActive = index === activeIndex;
        thumbnail.classList.toggle('thumbnail-selected', isActive);
        thumbnail.style.borderColor = isActive ? '#1A1A1A4D' : 'transparent';
      });
    }
  }

  if (!window.customElements.get('product-media-thumbnails')) {
    window.customElements.define('product-media-thumbnails', ProductMediaThumbnails);
  }

  class GeneralSlider extends HTMLElement {
  constructor() {
    
    super();
    this.logoPagination = this.querySelectorAll('.logo-item');
    this.pagination = this.querySelector('.slide-pagination')
    this.swiperInit();
  }
  swiperInit() {
    if(this.dataset.sliderType == 'normal'){
      const swiper = new Swiper(this, {
        slidesPerView: 1.1,
        spaceBetween: 24,
        breakpoints: {
          750: {
            slidesPerView: 2.1,
          },
          992: {
            slidesPerView: 3.1,
          }
        },
        navigation: {
          nextEl: this.previousElementSibling.querySelector('.swiperslide-button-next'),
          prevEl: this.previousElementSibling.querySelector('.swiperslide-button-prev'),
        },
        pagination: {
          el: this.pagination,
          clickable: true,
        },
      });
    }else{
      const swiper = new Swiper(this, {
        slidesPerView: 'auto',
        spaceBetween: 96,
        navigation: {
          nextEl: this.previousElementSibling.querySelector('.swiperslide-button-next'),
          prevEl: this.previousElementSibling.querySelector('.swiperslide-button-prev'),
        },
        pagination: {
          el: this.pagination,
          clickable: true,
        },
        on: {
            init: function() {

            },
            slideChange: function(e) {
              document.querySelector('[data-slide-bg]').style.backgroundPositionX = e.translate + 'px';
            }
        }
      });
    }
    
    
  }
}
if (!window.customElements.get('general-slider')) {
  window.customElements.define('general-slider', GeneralSlider);
}


class WithNavSlider extends HTMLElement {
  constructor() {
    super();
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.pagination = this.querySelector('.slide-pagination');
    this.slideWithNav = this.querySelector('.slider-with-nav');
    this.swiperNav = this.querySelector('.swiper-navigation');
    this.swiperInit();
  }
  swiperInit() {
    var swiper_nav = new Swiper(this.swiperNav, {
      loop: true,
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      watchSlidesProgress: true,
    });
    const swiper = new Swiper(this.slideWithNav, {
      loop: true,
      slidesPerView: 1,
      spaceBetween: 24,
      centeredSlides: true,
      breakpoints: {
        0: {
          slidesPerView: 'auto',
          centeredSlides: false,
        },
        750: {
          slidesPerView: 'auto',
          centeredSlides: true,
        }
      },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      pagination: {
        el: this.pagination,
        clickable: true
      },
      thumbs: {
        swiper: swiper_nav,
      }
    });

    // Sync: swiper_nav swipe → main swiper (use transitionEnd to avoid loop clone offset)
    swiper_nav.on('slideChangeTransitionEnd', function() {
      swiper.slideToLoop(swiper_nav.realIndex);
    });

    // Mobile: nav-progress-bar, evenly divided per slide
    var progressBar = this.querySelector('.nav-progress-bar');
    if (progressBar) {
      var totalSlides = swiper_nav.slides.length; // includes loop clones
      // Count original slides (exclude clones by checking swiper-slide-duplicate class)
      var originalSlides = this.swiperNav.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length;
      var updateProgress = function() {
        if (window.innerWidth > 748) return;
        if (originalSlides <= 1) { progressBar.style.width = '100%'; return; }
        var idx = swiper_nav.realIndex;
        // Evenly divided: each slide = 1/originalSlides of total width
        progressBar.style.width = ((idx + 1) / originalSlides * 100) + '%';
      };
      swiper_nav.on('slideChange transitionEnd', updateProgress);
      // fire once on init so first item has correct width
      swiper_nav.on('init', function() { setTimeout(updateProgress, 50); });
    }
    
  }
}
if (!window.customElements.get('withnav-slider')) {
  window.customElements.define('withnav-slider', WithNavSlider);
}

class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this.config = this.getConfig();
  }

  connectedCallback() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.swiperInit());
    } else {
      this.swiperInit();
    }
  }

  getConfig() {
    try {
      return JSON.parse(this.dataset.swiperConfig || '{}');
    } catch (error) {
      console.error('Invalid Swiper config JSON:', error);
      return {};
    }
  }

  swiperInit() {
    // Merge default config with custom config
    const defaultConfig = {
      slidesPerView: 4,
      spaceBetween: 24,
    };

    const config = {
      ...defaultConfig,
      ...this.config
    };

    this.swiper = new Swiper(this, config);
  }

  disconnectedCallback() {
    // Clean up Swiper instance if needed
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}

if (!window.customElements.get('swiper-slider')) {
  window.customElements.define('swiper-slider', SwiperSlider);
}

class CustomDropdown extends HTMLElement {
    constructor() {
        super();
        this.init();
    }

    init() {
        this.header = this.querySelector('.dropdown-header');
        this.list = this.querySelector('.dropdown-list');
        this.icon = this.querySelector('.dropdown-icon');
        this.closeButton = this.querySelector('.close-dropdown');
        this.options = this.querySelectorAll('.dropdown-options li');
        this.selectedTopic = this.querySelector('.selected-topic');
        
        this.bindEvents();
    }

    bindEvents() {
        this.header.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggle();
        });

        this.closeButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.close();
        });

        this.options.forEach(option => {
            option.addEventListener('click', (e) => {
                e.stopPropagation();
                this.selectOption(option);
            });
        });

        document.addEventListener('click', () => {
            this.close();
        });

        this.list.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }

    toggle() {
        if (this.list.classList.contains('active')) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.list.classList.add('active');
        // this.list.classList.remove('sd:hidden');
        if (this.icon) {
            this.icon.classList.add('active');
        }
    }

    close() {
        this.list.classList.remove('active');
        // this.list.classList.add('sd:hidden');
        if (this.icon) {
            this.icon.classList.remove('active');
        }
    }

    selectOption(option) {
        this.options.forEach(opt => opt.classList.remove('selected'));
        option.classList.add('selected');
        this.selectedTopic.textContent = option.textContent;
        this.close();
        
        // Get all dataset values from the selected option
        const dataset = {};
        for (const [key, value] of Object.entries(option.dataset)) {
            dataset[key] = value;
        }
        
        // Dispatch dropdownChange event with dataset
        this.dispatchEvent(new CustomEvent('dropdownChange', {
            detail: {
                selectedValue: option.textContent,
                dataset: dataset,
                element: option
            },
            bubbles: true
        }));
    }

    get value() {
        return this.selectedTopic.textContent;
    }

    set value(value) {
        const option = Array.from(this.options).find(opt => opt.textContent === value);
        if (option) {
            this.selectOption(option);
        }
    }

    // Method to get current selected option's dataset
    getSelectedDataset() {
        const selectedOption = this.querySelector('.dropdown-options li.selected');
        return selectedOption ? selectedOption.dataset : null;
    }

    connectedCallback() {
        this.close();
    }
}

if (!window.customElements.get('custom-dropdown')) {
    window.customElements.define('custom-dropdown', CustomDropdown);
}

class WhoCanJoin extends HTMLElement {
  constructor() {
    super();
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.progressBar = this.querySelector('.slider-pagination');
    this.swiperInit();
  }
  swiperInit() {
    const swiper = new Swiper(this.querySelector('.swiper'), {
      slidesPerView: 1,
      spaceBetween: 24,
      pagination: {
        el: this.progressBar,
        clickable: true,
      },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      breakpoints: {
        750: {
          slidesPerView: 2,
        },
        992: {
          slidesPerView: 3.15,
        }
      },
    });
  }
}
if (!window.customElements.get('who-can-join')) {
  window.customElements.define('who-can-join', WhoCanJoin);
}


class ageStageSlider extends HTMLElement {
  constructor() {
    super();
    this.nextButton = this.querySelector('.slide-next-btn');
    this.prevButton = this.querySelector('.slide-prev-btn');
    this.swiperInit();
  }
  swiperInit() {
    const swiper = new Swiper(this, {
      slidesPerView: 1,
      spaceBetween: this.dataset.spacing,
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      breakpoints: {
        750: {
          slidesPerView: 2,
        },
        992: {
          slidesPerView: 3,
        }
      },
    });

    const navlinks = document.querySelectorAll('a[data-carousel-nav]');
    const slides = this.querySelectorAll('.swiper-slide');

    function removeActiveClass() {
      navlinks.forEach(link => {
          link.classList.remove('active');
      });
    }

    function setActiveNav(index) {
      removeActiveClass();
      navlinks.forEach(link => {
        if (parseInt(link.getAttribute('data-carousel-nav')) === index) {
          link.classList.add('active');
        }
      });
    }

    // 点击 tab 导航到对应 slide
    navlinks.forEach(link => {
        link.addEventListener('click', function(event) {
          event.preventDefault();
          const navValue = this.getAttribute('data-carousel-nav');
          removeActiveClass();
          this.classList.add('active');
          swiper.slideTo(navValue);
        });
    });

    // 移动端：swiper 滑动切换时同步高亮对应 tab
    swiper.on('slideChange', function() {
      setActiveNav(swiper.activeIndex);
    });

    // PC端：鼠标移动到 swiper-slide 时高亮对应 tab
    slides.forEach((slide, index) => {
      slide.addEventListener('mouseenter', function() {
        setActiveNav(index);
      });
    });
  }
}
if (!window.customElements.get('age-stage-slider')) {
  window.customElements.define('age-stage-slider', ageStageSlider);
}